/**
 * Ruby AI Actions, Vibes & Ruby Tokens — admin client (spec 2026-09-27).
 *
 * Backend: the `pos-agents-ext` stack (admin pool, explicit super_admin claim).
 *   GET    /agents/admin/ai/catalog
 *   PUT    /agents/admin/ai/actions/{actionId}          merge patch, null clears
 *   PUT    /agents/admin/ai/vibes/{vibeId}              merge patch (+ announceNow)
 *   DELETE /agents/admin/ai/vibes/{vibeId}
 *   PUT    /agents/admin/ai/providers/{providerId}
 *   PUT    /agents/admin/ai/providers/{providerId}/models/{modelId}
 *   PUT    /agents/admin/ai/providers/{providerId}/key  write-only SSM SecureString
 *   POST   /agents/admin/ai/sample-run                  free, logged as admin_test
 *   GET    /agents/admin/ai/usage?from&to
 *
 * Host is derived per stage exactly like pos-agents (transport.ts) — never
 * a hardcoded dev URL. Units: action/vibe min/maxTokens are RUBY TOKENS;
 * every ledger/usage `credits` value is STORED credits (x1000 to display).
 */

import { authedRequest } from '../apiService';
import { AGENTICPOS_DOMAINS, detectStage, type Stage } from './transport';
import type { AiModelDto, AiProviderDto, AiRoleDto, AiRegistryConfigDto, AiUsageCountsDto } from './registry';

/** A provider/model pair — action step default/fallback, vibe override. */
export interface ModelRefDto { providerId: string; modelId: string }

/**
 * A multi-step pipeline step (e.g. draw → edit background). Absent on an
 * action = single-step, using the action's own top-level providerId/modelId.
 */
export interface AiActionStepDto extends ModelRefDto {
  id: string;
  capability: string;
  fallbacks: ModelRefDto[];
}

export function agentsExtUrl(path: string, stage?: Stage): string {
  return `https://pos-agents-ext.api.${AGENTICPOS_DOMAINS[stage ?? detectStage()]}${path}`;
}

// ---------- rows ----------

export type AiActionGroupId = 'text' | 'image' | 'video' | 'voice';

export interface AiActionDto {
  actionId: string;
  label: string;
  group: AiActionGroupId;
  enabled: boolean;
  description?: string;
  providerId: string;
  modelId: string;
  fallbackProviderId?: string;
  fallbackModelId?: string;
  multiplier: number;
  /** Ruby Tokens (display unit). */
  minTokens: number;
  /** Ruby Tokens (display unit). Also the reserve. */
  maxTokens: number;
  featureId: string;
  trigger?: { surface: string; file?: string };
  runs?: { service: string; endpoint?: string };
  createdAt?: string;
  updatedAt?: string;
  // --- 2026-10-04, Ruby Generation Engine (all optional, additive) ---
  /** Multi-step pipeline; absent = single-step via providerId/modelId above. */
  steps?: AiActionStepDto[];
  /** Target entity types this action can run against. */
  targetTypes?: string[];
}

export interface AiVibeDto {
  vibeId: string;
  label: string;
  color?: string;
  enabled: boolean;
  actionIds: string[];
  defaultFor: string[];
  background: string;
  context: string;
  promptCustomisation: string;
  providerId?: string;
  modelId?: string;
  multiplier?: number;
  season?: { fromMonthDay: string; toMonthDay: string };
  featureId?: string;
  announceNewDays?: number;
  announcedAt?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  // --- 2026-10-04, Ruby Generation Engine (all optional, additive) ---
  /** Per-action model override, keyed by actionId. Absent = the action/step default. */
  modelOverrides?: Record<string, ModelRefDto>;
  /** When true, this vibe edits the existing scene rather than regenerating it. */
  keepScene?: boolean;
}

export interface AiCatalogResponse {
  providers: AiProviderDto[];
  models: AiModelDto[];
  roles: AiRoleDto[];
  actions: AiActionDto[];
  vibes: AiVibeDto[];
  config: AiRegistryConfigDto;
}

const extGet = <T>(path: string) => authedRequest<T>(agentsExtUrl(path));
const extSend = <T>(path: string, method: 'PUT' | 'POST' | 'DELETE', body?: unknown) =>
  authedRequest<T>(agentsExtUrl(path), {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

const seg = encodeURIComponent;

export async function getRubyAiCatalog(): Promise<AiCatalogResponse> {
  const b = await extGet<Partial<AiCatalogResponse>>('/agents/admin/ai/catalog');
  return {
    providers: b.providers ?? [],
    models: b.models ?? [],
    roles: b.roles ?? [],
    actions: (b.actions ?? []).map((a) => ({ ...a, enabled: a.enabled !== false })),
    vibes: (b.vibes ?? []).map((v) => ({
      ...v,
      enabled: v.enabled !== false,
      actionIds: v.actionIds ?? [],
      defaultFor: v.defaultFor ?? [],
      background: v.background ?? '',
      context: v.context ?? '',
      promptCustomisation: v.promptCustomisation ?? '',
    })),
    config: b.config ?? { creditUnitPriceUsd: 0.01, minPurchaseCredits: 50 },
  };
}

export function putAiAction(actionId: string, patch: Record<string, unknown>) {
  return extSend<{ row: AiActionDto; created: boolean }>(`/agents/admin/ai/actions/${seg(actionId)}`, 'PUT', patch);
}

export function putAiVibe(vibeId: string, patch: Record<string, unknown>) {
  return extSend<{ row: AiVibeDto; created: boolean; defaultMovedFrom: string[] }>(
    `/agents/admin/ai/vibes/${seg(vibeId)}`, 'PUT', patch,
  );
}

export function deleteAiVibe(vibeId: string) {
  return extSend<{ deleted: boolean }>(`/agents/admin/ai/vibes/${seg(vibeId)}`, 'DELETE');
}

export interface ProviderPatch {
  label?: string;
  vendor?: string;
  enabled?: boolean;
  authType?: 'header' | 'query' | 'serviceAccount';
  authHeaderName?: string | null;
  authPrefix?: string | null;
  endpointTemplate?: string | null;
  credentialsRef?: string;
}

export function putAiProvider(providerId: string, patch: ProviderPatch) {
  return extSend<{ row: AiProviderDto; created: boolean }>(`/agents/admin/ai/providers/${seg(providerId)}`, 'PUT', patch);
}

export interface ModelPatch {
  label?: string;
  enabled?: boolean;
  capabilities?: string[];
  unitPrices?: Record<string, number>;
  // --- 2026-10-04, Ruby Generation Engine (additive) ---
  /** Vendor model version/snapshot id. `null` clears it. */
  version?: string | null;
  /** Typical usage for cost-preview UIs. `null` clears it. */
  typicalUsage?: AiUsageCountsDto | null;
}

export function putAiModel(providerId: string, modelId: string, patch: ModelPatch) {
  return extSend<{ row: AiModelDto; created: boolean }>(
    `/agents/admin/ai/providers/${seg(providerId)}/models/${seg(modelId)}`, 'PUT', patch,
  );
}

/**
 * Write-only. The key goes straight to the SSM SecureString at the provider's
 * credentialsRef; the response never carries it and callers must drop it
 * from state the moment this resolves.
 */
export function putAiProviderKey(providerId: string, key: string) {
  return extSend<{ ok: boolean; updatedAt: string; credentialsRef: string; version: number | null }>(
    `/agents/admin/ai/providers/${seg(providerId)}/key`, 'PUT', { key },
  );
}

// ---------- sample run ----------

export interface SampleInput {
  name: string;
  category: string;
  brand?: string | null;
  context?: string | null;
}

export interface SampleStep {
  type: string | null;
  providerId: string | null;
  modelId: string | null;
  inputTokens: number;
  outputTokens: number;
  searchCalls: number;
  vendorCostUsd: number;
}

export interface SampleBreakdown {
  providerId: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  searchCalls: number;
  vendorCostUsd: number;
  multiplier: number;
  /** STORED credits a tenant would pay. */
  credits: number;
  rubyTokens: number;
  minRubyTokens: number;
  maxRubyTokens: number;
  unclampedCredits: number;
  steps: SampleStep[];
}

export interface SampleRunResponse {
  output: string;
  breakdown: SampleBreakdown;
  runId?: string;
  actionId: string;
  vibeId: string | null;
  path?: string;
  actionEnabled?: boolean;
  vibeEnabled?: boolean | null;
  charged: false;
  actorType: 'admin_test';
  actorId: string;
  latencyMs?: number;
}

export function runAiSample(body: { actionId: string; vibeId?: string | null; sample: SampleInput }) {
  return extSend<SampleRunResponse>('/agents/admin/ai/sample-run', 'POST', body);
}

// ---------- usage & margin ----------

export interface UsageAggRow {
  runs: number;
  calls: number;
  credits: number;
  rubyTokens: number;
  vendorCostUsd: number;
  revenueUsd: number;
  marginPct: number | null;
}

export interface UsageByAction extends UsageAggRow { actionId: string; label: string }
export interface UsageByVibe extends UsageAggRow { actionId: string; vibeId: string; label: string }
export interface UsageByTenant {
  tenantId: string;
  tenantName?: string;
  actionId: string;
  vibeId: string;
  model: string;
  models: string[];
  runs: number;
  calls: number;
  credits: number;
  rubyTokens: number;
  vendorCostUsd: number;
  costUsd?: number;
}
export interface UsageAdminTest {
  actorId: string;
  actionId: string;
  vibeId: string;
  runs: number;
  calls: number;
  vendorCostUsd: number;
  models: string[];
}
export interface VendorSpend {
  totalCostUsd: number;
  byVendor: Array<{
    vendor: string; calls: number; costUsd: number; sharePct: number;
    providers?: Array<{ providerId: string; calls: number; costUsd: number }>;
  }>;
  byActorType: Array<{ actorType: string; runs: number; calls: number; costUsd: number }>;
  unmappedCostUsd: number;
}

export interface RubyAiUsageResponse {
  range?: { fromIso?: string; toIso?: string };
  creditUnitPriceUsd: number;
  totals: {
    rubyTokensCharged: number;
    credits: number;
    vendorCostUsd: number;
    revenueUsd: number;
    marginPct: number | null;
    allowanceCredits: number;
    purchasedCredits: number;
    runs: number;
    calls: number;
  };
  byAction: UsageByAction[];
  byVibe: UsageByVibe[];
  byTenant: UsageByTenant[];
  adminTests: UsageAdminTest[];
  vendorSpend: VendorSpend;
}

export async function getRubyAiUsage(range: { fromIso?: string; toIso?: string }): Promise<RubyAiUsageResponse> {
  const q = new URLSearchParams();
  if (range.fromIso) q.set('from', range.fromIso);
  if (range.toIso) q.set('to', range.toIso);
  const qs = q.toString();
  const b = await extGet<Partial<RubyAiUsageResponse>>(`/agents/admin/ai/usage${qs ? `?${qs}` : ''}`);
  return {
    range: b.range,
    creditUnitPriceUsd: b.creditUnitPriceUsd ?? 0.01,
    totals: {
      rubyTokensCharged: 0, credits: 0, vendorCostUsd: 0, revenueUsd: 0, marginPct: null,
      allowanceCredits: 0, purchasedCredits: 0, runs: 0, calls: 0,
      ...(b.totals ?? {}),
    },
    byAction: b.byAction ?? [],
    byVibe: b.byVibe ?? [],
    byTenant: b.byTenant ?? [],
    adminTests: b.adminTests ?? [],
    vendorSpend: {
      totalCostUsd: 0, byVendor: [], byActorType: [], unmappedCostUsd: 0,
      ...(b.vendorSpend ?? {}),
    },
  };
}

/**
 * A readable message from an authedRequest refusal: "CODE — message", plus
 * any `problems` list the server attached (e.g. 400 CAPABILITY_MISMATCH from
 * a step/vibe-override PUT: each problem names the model and the capability
 * it lacks) joined on, so the picker's inline error says exactly which model
 * was rejected and why — not just the error code.
 */
export function rubyAiErrorText(err: unknown, fallback = 'Request failed'): string {
  const e = err as { message?: string; serverMessage?: string; code?: string; problems?: unknown } | null;
  if (!e) return fallback;
  const base = e.serverMessage && e.code && e.serverMessage !== e.code
    ? `${e.code} — ${e.serverMessage}`
    : (e.serverMessage || e.message || fallback);
  if (Array.isArray(e.problems) && e.problems.length) {
    return `${base}: ${e.problems.map((p) => String(p)).join('; ')}`;
  }
  return base;
}
